import { Component, Input, OnInit, OnDestroy, ViewChild, ElementRef, Output, EventEmitter } from '@angular/core';
import { FormGroup, FormBuilder } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil, take } from 'rxjs/operators';
import {
  DateTimeCellComponent,
  GeometryCellComponent,
  TextCellComponent,
  AutoIncrementCellComponent,
  UserCellComponent,
  NumberCellComponent,
  UrlCellComponent,
  PicklistCellComponent,
  MultiPicklistCellComponent,
  ImageCellComponent,
  WorkflowCellComponent,
  BooleanCellComponent,
  ColorCellComponent,
  DistanceCellComponent,
  CultureCodeCellComponent,
  PhoneCellComponent,
  DurationCellComponent,
  EnumerationCellComponent,
  BusinessUnitsCellComponent,
  ReferenceCellComponent
} from 'src/app/modules/core/components/ag-grid';
import { Document } from 'src/app/modules/core/store/models/document.model';
import {
  AgGridEvent,
  GridApi,
  IServerSideDatasource,
  FirstDataRenderedEvent,
  GetLocaleTextParams,
  GetRowIdParams,
  RowSelectionOptions,
  ColDef,
  ModelUpdatedEvent
} from 'ag-grid-enterprise';
import { v4 as uuidv4 } from 'uuid';
import { LinkCellComponent } from 'src/app/modules/core/components/ag-grid/link-cell/link-cell.component';
import { ActionCellComponent } from 'src/app/modules/core/components/ag-grid/action-cell/action-cell.component';
import { PaginationChangedEvent, ColumnState } from 'ag-grid-enterprise';
import { TableStateStorage } from 'src/app/modules/core/components/ag-grid/table-state-service';
import { Dataset } from 'src/app/modules/core/store/models/dataset.model';
import { TranslateService } from '@ngx-translate/core';
import { ExportTemplate } from 'src/app/modules/management/store/datasets/dataset.model';
import { ActionMenuComponent } from '../action-menu/action-menu.component';
import { GridHelperService } from '../../services/grid-helper.service';
import { FilterGroup } from '../../store/models';

@Component({
  selector: 'topo-data-table-server',
  templateUrl: './data-table.component.html',
  styleUrls: ['./data-table.component.scss'],
  standalone: false
})
export class DataTableServerComponent implements OnInit, OnDestroy {
  @ViewChild('tableContainer') tableContainer: ElementRef;
  @ViewChild('dragHandleTop') dragHandleTop: ElementRef;
  @ViewChild('dragHandleCorner') dragHandleCorner: ElementRef;
  @ViewChild('selectedEntityActionsContainer') selectedEntityActionsContainer: ElementRef;
  @ViewChild('menu') actionsMenu: ActionMenuComponent;

  @Input() set isActive(value: boolean) {
    if (!value) {
      this._columns = [];

      this.hideSearch();
    }
    this._isActive = value;
  }
  get isActive(): boolean {
    return this._isActive;
  }
  _isActive = false;
  @Input() isTableLoading: boolean;
  @Input() set leftDrawerOpen(value: boolean) {
    this._leftDrawerOpen = value;
    this.leftDrawerOpenClass = this._leftDrawerOpen ? 'is-left-drawer-open' : '';
  }
  get leftDrawerOpen(): boolean {
    return this._leftDrawerOpen;
  }
  private _leftDrawerOpen = false;

  @Input() rightDrawerOpen: boolean;
  @Input() htmlColorCode = '#333333';
  @Input() headerLabel: string = '';
  @Input() headerIcon: string = '';
  @Input() showFilter = true;
  @Input() isFloating = false;
  @Input() defaultHeight = 360;
  @Input() height?: number = null;
  @Input() rawHeight?: string = null;
  @Input() width?: number = null;
  @Input() embedded: boolean;
  @Input() singleDatasetMode: boolean;
  @Input() tableHidden: boolean = false;
  @Input() hidePageLinks: boolean;
  @Input() hidePager: boolean;
  @Input() showButtonBar: boolean = true;
  @Input() rowHeight: number;
  @Input() enableInfiniteScroll: boolean;
  @Input() enableServerSort: boolean;
  @Input() enableServerFilter: boolean;
  @Input() showSelectedCount: boolean;
  @Input() tabs?: {
    uId: string;
    label: string;
    iconSettings: any;
  }[];
  @Input() tableType?: string;
  _columns: any[];
  @Input() set columns(value: any) {
    this._columns = value;
    this.gridApi?.setGridOption('columnDefs', []); // ag-grid bug - force columns refresh
    this.gridApi?.setGridOption('columnDefs', this._columns);
  }

  get columns(): any[] {
    return this._columns;
  }

  @Input() getRowId: (params: GetRowIdParams) => any = params => params?.data?.uid ?? params?.data?.uId;

  @Input() set filterModel(value: any) {
    if (!!value) {
      this.gridApi?.setFilterModel(value);
    }
  }

  @Input() dataset?: Dataset;
  @Input() datasetTemplates?: ExportTemplate[];
  @Input() skip = 0;
  @Input() take = 25;
  @Input() defaultPageSize = 25;
  @Input() totalCount = 0;
  @Input() showHeader: boolean;
  @Input() collapseHeader: boolean = false;
  @Input() showSearch: boolean;
  @Input() showMenu: boolean;
  @Input() showDownload: boolean = false;
  @Input() showRowGroups: boolean;
  @Input() hideDownloadButton: boolean;
  @Input() autoHide: boolean = false;
  @Input() showLoading: boolean = false;
  @Input() hideStateMenu: boolean = false;
  @Input() steps = 2;
  @Input() maxPagesForShowAll = 10;
  @Input() showNewButton = false;
  @Input() autoFitColumns = true;
  @Input() needsRefresh: boolean = false;
  @Input() newLabel = null;
  @Input() showSystemFields: boolean = false;
  @Input() selectedTab: string;
  @Input() datasource: IServerSideDatasource;
  @Input() getServerSideGroupLevelParams: (params) => any = null;
  @Input() autoGroupColumnDef;
  @Input() emptyMessage: string;

  @Output() hide = new EventEmitter<void>();
  @Output() selectDocuments = new EventEmitter<Document[]>();
  @Output() selectDocument = new EventEmitter<Document>();
  @Output() zoomToDocument = new EventEmitter<Document>();
  @Output() takeChanged = new EventEmitter<number>();
  @Output() tabClick = new EventEmitter<string>();
  @Output() paginate = new EventEmitter<number>();
  @Output() refreshClick = new EventEmitter<void>();
  @Output() newClick = new EventEmitter<void>();
  @Output() sort = new EventEmitter<SortEvent>();
  @Output() exportToDocument = new EventEmitter<ExportToDocumentEvent>();
  @Output() columnsChanged = new EventEmitter<ColumnState[]>();
  @Output() groupsChanged = new EventEmitter<ColumnState[]>();
  @Output() searchFilterChanged = new EventEmitter<FilterGroup>();
  @Output() columnFiltersChanged = new EventEmitter<any>();
  @Output() resetState = new EventEmitter();
  @Output() saveColumns = new EventEmitter<string[]>();
  @Output() exportToExcel = new EventEmitter();
  @Output() firstDataRenderedCalled = new EventEmitter();
  @Output() storeUpdatedCalled = new EventEmitter();
  @Output() rowsChanged = new EventEmitter<ModelUpdatedEvent>();
  @Output() rowSelected = new EventEmitter<any>();
  @Output() searchVisibilityChanged = new EventEmitter<boolean>();

  frameworkComponents = {
    dateTimeCellComponent: DateTimeCellComponent,
    geometryCellComponent: GeometryCellComponent,
    textCellComponent: TextCellComponent,
    autoIncrementCellComponent: AutoIncrementCellComponent,
    userCellComponent: UserCellComponent,
    numberCellComponent: NumberCellComponent,
    urlCellComponent: UrlCellComponent,
    picklistCellComponent: PicklistCellComponent,
    multiPicklistCellComponent: MultiPicklistCellComponent,
    imageCellComponent: ImageCellComponent,
    workflowCellComponent: WorkflowCellComponent,
    booleanCellComponent: BooleanCellComponent,
    colorCellComponent: ColorCellComponent,
    distanceCellComponent: DistanceCellComponent,
    linkCellComponent: LinkCellComponent,
    actionCellComponent: ActionCellComponent,
    cultureCodeCellComponent: CultureCodeCellComponent,
    phoneCellComponent: PhoneCellComponent,
    durationCellComponent: DurationCellComponent,
    enumerationCellComponent: EnumerationCellComponent,
    businessUnitsCellComponent: BusinessUnitsCellComponent,
    referenceCellComponent: ReferenceCellComponent
  };

  rowSelection: RowSelectionOptions = {
    mode: 'multiRow',
    groupSelects: 'filteredDescendants',
    enableClickSelection: false,
    checkboxes: false,
    headerCheckbox: false
  };

  form: FormGroup;
  dragPosition = null;
  leftDrawerOpenClass = '';
  searchVisible = false;
  selectedCount: number = 0;
  title = 'my-app';
  sortHasBeenSet = false;
  showRowGroup = false;
  suppressNoRowsOverlay: boolean = true;
  hidden: boolean = false;
  showEditColumns = false;
  tableColumns: any[];
  isDataLoading: boolean = true;
  id: string;
  defaultColDef = {
    sortable: true
  };

  groupRowRendererParams = {
    checkbox: true,
    rowDrag: true
  };

  private destroy$: Subject<void> = new Subject();
  public gridApi: GridApi;

  constructor(
    private fb: FormBuilder,
    private translateService: TranslateService,
    private gridHelper: GridHelperService
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      filter: ['']
    });

    this.id = 'a' + uuidv4();

    this.form
      .get('filter')
      .valueChanges.pipe(debounceTime(500), takeUntil(this.destroy$))
      .subscribe(val => {
        if (this._isActive) {
          this.generateSearchFilter(val);
          this.refreshGrid();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
  }

  refreshGrid() {
    this.gridApi?.refreshServerSide();
  }

  get groupShowType(): string {
    return this.showRowGroups !== false ? 'always' : 'never';
  }

  generateSearchFilter(searchTerm: string) {
    const filterGroup = this.gridHelper.generateSearchClause(searchTerm, this.columns);
    this.searchFilterChanged.emit(filterGroup);
  }

  gridReady(params: AgGridEvent<any>): void {
    params.api!.setGridOption('serverSideDatasource', this.datasource);
    this.gridApi = params.api;

    this.gridApi.addEventListener('stateUpdated', e => {
      this.storeUpdated(e);
    });

    this.gridApi.addEventListener('modelUpdated', e => {
      this.rowsChanged.emit(e);
    });

    if (this.showLoading) {
      this.suppressNoRowsOverlay = true;
      this.doShowLoading(this.gridApi);
    }
  }

  storeUpdated(event: any): void {
    this.storeUpdatedCalled.emit();
    this.isDataLoading = true;
  }

  firstDataRendered(params: FirstDataRenderedEvent<any>) {
    if (this.autoFitColumns === true) {
      this.gridApi?.sizeColumnsToFit();
    }

    this.firstDataRenderedCalled.emit();
  }

  tableHeightCalc() {
    if (!!this.rawHeight) {
      return this.rawHeight;
    } else if (!!this.height) {
      return `${this.height}px`;
    } else {
      const hasTabs = this.tabs?.length > 0;
      const offset = this.showHeader ? (hasTabs ? 288 : 238) : hasTabs ? 200 : 150;
      const selectedEntityActionsContainerHeight = this.selectedEntityActionsContainer?.nativeElement.offsetHeight ?? 0;
      const subtrahend = offset + selectedEntityActionsContainerHeight;
      // * NOTE * - this is a bandaid solution and should NOT be dictating height based off screen height nor using hardcoded pixel values based on other elements on the screen.
      // Left it in this state here since this component can be "embedded", but didn't want to break other instances of this being used.
      return `calc(100vh - ${subtrahend}px)`;
    }
  }

  onRowSelected(event: any): void {
    this.rowSelected.emit(event);
  }

  onSelectDocument(document: any) {
    this.selectDocument.emit({ document });
  }

  hideActions() {
    const cellRendererInstances = this.gridApi?.getCellRendererInstances();
    cellRendererInstances
      .map(c => {
        var cellRenderer: any = c;
        try {
          return cellRenderer.getFrameworkComponentInstance();
        } catch (e) {
          return null;
        }
      })
      .filter(r => {
        return r !== null && r instanceof ActionCellComponent;
      })
      .map(r => {
        r.hideActions();
      });
  }

  hideTable() {
    this.hide.emit();
  }

  exportToExcelClicked(): void {
    const columns = this.gridApi
      ?.getColumnState()
      ?.filter(c => c.colId !== '_selectionChecks' && c.colId !== '_actions')
      .map(c => c.colId);
    this.exportToExcel.emit(columns);
  }

  exportToDocumentClicked(template: ExportTemplate, type: ExportToDocumentType) {
    if (this.exportToDocument) {
      this.exportToDocument.emit({ template: template, type: type });

      setTimeout(() => {
        this.actionsMenu.hideMenu();
      }, 100);
    }
  }

  editColumns() {
    this.tableColumns = this.gridApi?.getColumnDefs();
    this.showEditColumns = true;
  }

  closeEditColumns() {
    this.showEditColumns = false;
  }
  /*
  columnsGenerator = (columnNames: string[]) => {
    if (!!this.dataset && this.generateColumns) {
      return this.generateColumns(columnNames, this.dataset);
    } else {
      return null;
    }
  };
*/
  saveEditColumns(columnNames: string[]) {
    this.saveColumns.emit(columnNames);
    this.columnsChanged.emit(this.gridApi.getColumnState());

    this.showEditColumns = false;
  }

  resetEditColumns(): void {
    this.showEditColumns = false;
    this.gridApi?.setGridOption('columnDefs', []);
    this.gridApi?.setGridOption('columnDefs', this.columns);
    this.columnsChanged.emit(this.gridApi.getColumnState());
  }

  onResetState() {
    this.showRowGroup = false;
    this.resetState.emit();
  }

  groupRows(value?: boolean) {
    if (value !== undefined) {
      this.showRowGroup = value === true;
    } else {
      this.showRowGroup = !this.showRowGroup;
    }

    if (this.selectedCount > 0) {
      this.showRowGroup = false;
    }
    let groupElement = document.querySelector(`#${this.id} .ag-column-drop-wrapper`) as HTMLElement;
    if (!!groupElement) {
      groupElement.style.display = this.showRowGroup ? 'block' : 'none';
    }
  }

  selectionChanged($event) {
    this.groupRows(false);

    const nodes = this.gridApi?.getSelectedNodes();

    this.selectedCount = nodes.filter(r => r.rowIndex !== null)?.length ?? 0;

    const documents = nodes.map(n => n.data);

    this.selectDocuments.emit(documents);
  }

  rowDoubleClicked($event) {
    const document = $event.data;
    this.zoomToDocument.emit(document);
  }

  tabClicked(uId: string) {
    this.gridApi.deselectAll();
    this.tabClick.emit(uId);
  }

  onPaginate(skip: number) {
    const pageNumber = Math.floor(skip / this.take);

    this.gridApi?.paginationGoToPage(pageNumber);
    this.skip = skip;
    this.hidden = false;
    this.paginate.emit(skip);
  }

  onTakeChanged(takeNumber: number) {
    this.gridApi?.setGridOption('paginationPageSize', takeNumber);
    this.take = takeNumber;
    this.skip = 0;
    this.hidden = false;
    this.takeChanged.emit(takeNumber);
    this.paginate.emit(0);
  }

  onNewClick(): void {
    this.newClick.emit();
  }

  onRefreshClick(): void {
    this.refreshClick.emit();
  }

  private doShowLoading(api: GridApi): void {
    api?.setGridOption('loading', true);
    setTimeout(() => {
      api?.setGridOption('loading', false);
      this.isDataLoading = false;
    }, 2500);
  }

  getLocaleText = (params: GetLocaleTextParams): string => {
    return this.translateService.instant(`AG-GRID.${params.key}`);
  };

  onShowSearch(): void {
    this.searchVisible = true;
    this.searchVisibilityChanged?.emit(true);

    const searchFields = document.getElementsByClassName('search-text-field');
    if (searchFields?.length > 0) {
      const search = searchFields[0] as HTMLInputElement;
      search.focus();
    }
  }

  paginationChanged(event: PaginationChangedEvent) {
    const page = event.api.paginationGetCurrentPage();
    this.skip = page * this.take;
    this.totalCount = event.api.getDisplayedRowCount();
  }

  hideSearch(): void {
    this.gridApi?.resetQuickFilter();
    this.form?.get('filter')?.reset();
    this.searchVisible = false;
    this.searchVisibilityChanged?.emit(false);
  }

  resetFilter(): void {
    this.gridApi?.resetQuickFilter();
    this.form?.get('filter')?.reset();
  }

  columnResized(event: any): void {
    if (event.source !== 'api' && event.source !== 'gridOptionsChanged') {
      this.columnsChanged.emit(this.gridApi.getColumnState());
    }
  }

  filterChanged(event: any): void {
    if (event.afterFloatingFilter !== undefined) {
      const model = event.api.getFilterModel();
      this.columnFiltersChanged.emit(model);
    }
  }

  dragStopped(event: any): void {
    if (event.source !== 'api' && event.source !== 'gridOptionsChanged') {
      this.columnsChanged.emit(this.gridApi.getColumnState());
    }
  }

  groupChanged(event: any): void {
    if (event.source !== 'api' && event.source !== 'gridOptionsChanged') {
      this.groupsChanged.emit(this.gridApi.getColumnState());
    }
  }

  sortChanged(event: any): void {
    const sortedColumnStates = this.gridApi?.getColumnState()?.filter(column => !!column.sort);
    const sortedColumnState = sortedColumnStates.length > 0 ? sortedColumnStates[0] : null;

    if (event.source !== 'api' && event.source !== 'gridOptionsChanged') {
      this.columnsChanged.emit(this.gridApi.getColumnState());
    }

    if (!sortedColumnState) {
      this.sort.emit(null);
      return;
    }

    const sortedColumn = this.gridApi?.getColumn(sortedColumnState.colId);
    if (!sortedColumn) {
      this.sort.emit(null);
      return;
    }

    const colDef = this.gridApi?.getColumnDef(sortedColumn);
    if (!colDef) {
      this.sort.emit(null);
      return;
    }

    var systemField = colDef.field;

    this.sort.emit({
      sortField: systemField,
      sortDirection: sortedColumnState.sort
    });
  }
}

export interface SortEvent {
  sortField: string;
  sortDirection: string;
}

export interface PersistOptions {
  stateKey?: string;
  storage?: TableStateStorage;
}

export interface ExportToDocumentEvent {
  template?: ExportTemplate;
  documents?: Document[];
  type?: ExportToDocumentType;
}

export enum ExportToDocumentType {
  word = 1,
  pdf = 2
}
